import { useState, useEffect, useRef } from 'react';
import { searchMedications, groupByCategory } from '../data/medications';

// Shared styles matching ASCModule.jsx dark theme
const BASE_INPUT = {
  background: 'var(--navy)',
  border: '1px solid var(--border)',
  borderRadius: 5,
  color: 'var(--text-1)',
  fontSize: 13,
  padding: '8px 10px',
  outline: 'none',
};

const LABEL_ST = {
  fontSize: 10,
  color: 'var(--text-2)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  display: 'block',
  marginBottom: 4,
};

// ── SpinnerInput ──────────────────────────────────────────────────────────────
// Numeric field with ± buttons and click-to-type centre display.
// Props: label?, value (string), onChange, min?, max?, step? (default 1), unit?, width? (px), noLabel?
export function SpinnerInput({ label, value, onChange, min, max, step = 1, unit, width = 110, noLabel }) {
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');

  const num = parseFloat(value);

  const clamp = (n) => {
    let v = parseFloat(n);
    if (!isNaN(min)) v = Math.max(min, v);
    if (!isNaN(max)) v = Math.min(max, v);
    return step < 1 ? v.toFixed(1) : String(Math.round(v));
  };

  const inc = (e) => {
    e.preventDefault();
    const n = isNaN(num) ? (min ?? 0) : num + step;
    onChange(clamp(n));
  };

  const dec = (e) => {
    e.preventDefault();
    const n = isNaN(num) ? (min ?? 0) : num - step;
    onChange(clamp(n));
  };

  const displayVal = value !== '' && value !== undefined
    ? (step < 1 ? parseFloat(value).toFixed(1) : value)
    : '';

  const btnSt = {
    width: 32, minHeight: 40, background: 'var(--navy-4)', border: 'none',
    color: 'var(--text-2)', fontSize: 20, cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    userSelect: 'none', WebkitUserSelect: 'none', lineHeight: 1,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {!noLabel && label && (
        <label style={LABEL_ST}>
          {label}{unit && <span style={{ color: 'var(--text-3)', marginLeft: 4 }}>({unit})</span>}
        </label>
      )}
      <div style={{
        display: 'flex', alignItems: 'stretch', borderRadius: 6, overflow: 'hidden',
        border: '1px solid var(--border)', width,
      }}>
        <button onPointerDown={dec} style={btnSt}>−</button>
        {typing ? (
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => {
              setTyping(false);
              const n = parseFloat(draft);
              if (!isNaN(n)) onChange(clamp(n));
              else if (draft === '') onChange('');
            }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') e.target.blur(); }}
            style={{ flex: 1, textAlign: 'center', background: 'var(--navy)', border: 'none', color: 'var(--text-1)', fontSize: 13, outline: 'none', minWidth: 0 }}
          />
        ) : (
          <div
            onClick={() => { setTyping(true); setDraft(value || ''); }}
            style={{
              flex: 1, textAlign: 'center', background: 'var(--navy-2)',
              color: displayVal ? 'var(--text-1)' : 'var(--text-3)', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'text', userSelect: 'none', WebkitUserSelect: 'none', minWidth: 0,
            }}
          >
            {displayVal || '—'}
          </div>
        )}
        <button onPointerDown={inc} style={btnSt}>+</button>
      </div>
    </div>
  );
}

// ── BPInput ───────────────────────────────────────────────────────────────────
// Blood pressure as "SBP/DBP" string. Two SpinnerInputs joined by "/".
export function BPInput({ label = 'BP (mmHg)', value, onChange }) {
  const raw = value || '';
  const slash = raw.indexOf('/');
  const sbp = slash >= 0 ? raw.slice(0, slash) : raw;
  const dbp = slash >= 0 ? raw.slice(slash + 1) : '';

  const update = (newSbp, newDbp) => {
    const s = newSbp !== undefined ? newSbp : sbp;
    const d = newDbp !== undefined ? newDbp : dbp;
    onChange((s || d) ? `${s}/${d}` : '');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={LABEL_ST}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <SpinnerInput value={sbp} onChange={v => update(v, undefined)} min={50} max={240} width={90} noLabel />
        <span style={{ color: 'var(--text-3)', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>/</span>
        <SpinnerInput value={dbp} onChange={v => update(undefined, v)} min={30} max={150} width={90} noLabel />
      </div>
    </div>
  );
}

// ── PainScore ─────────────────────────────────────────────────────────────────
// 0–10 tappable colour-coded buttons. value is a string "0"–"10".
export function PainScore({ label = 'Pain Score (0–10)', value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={LABEL_ST}>{label}</label>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => {
          const sel = String(n) === String(value);
          const color = n <= 3 ? '#10b981' : n <= 6 ? '#f59e0b' : '#ef4444';
          const alphaPart = n <= 3 ? '16,185,129' : n <= 6 ? '245,158,11' : '239,68,68';
          return (
            <button
              key={n}
              onClick={() => onChange(String(n))}
              style={{
                width: 40, height: 40, borderRadius: 7, fontSize: 14, cursor: 'pointer',
                border: `2px solid ${sel ? color : 'var(--border)'}`,
                background: sel ? `rgba(${alphaPart},0.2)` : 'var(--navy-4)',
                color: sel ? color : 'var(--text-3)',
                fontWeight: sel ? 700 : 400, flexShrink: 0,
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── TimePicker ────────────────────────────────────────────────────────────────
// Stores and emits "HH:MM" 24h. Displays 12h with AM/PM selects.
// "Now" sets current time. "···" falls back to native <input type="time">.

function parse24h(val) {
  if (!val) return '';
  if (/^\d{2}:\d{2}$/.test(val)) return val;
  // Handle legacy "3:45 PM" strings from old toLocaleTimeString init
  const m = val.match(/^(\d+):(\d+)\s*(AM|PM)/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = m[2];
    const ap = m[3].toUpperCase();
    if (ap === 'AM' && h === 12) h = 0;
    else if (ap === 'PM' && h !== 12) h += 12;
    return `${String(h).padStart(2, '0')}:${min}`;
  }
  return val;
}

export function TimePicker({ label, value, onChange }) {
  const [typing, setTyping] = useState(false);
  const v = parse24h(value);

  let h12 = '', mStr = '00', ampm = 'AM';
  if (v) {
    const [hh, mm] = v.split(':');
    const h24 = parseInt(hh, 10);
    mStr = mm;
    if (h24 === 0)       { h12 = '12'; ampm = 'AM'; }
    else if (h24 < 12)   { h12 = String(h24); ampm = 'AM'; }
    else if (h24 === 12) { h12 = '12'; ampm = 'PM'; }
    else                 { h12 = String(h24 - 12); ampm = 'PM'; }
  }

  const commit = (newH12, newM, newAp) => {
    let h = parseInt(newH12 || '12', 10);
    if (newAp === 'AM') { if (h === 12) h = 0; }
    else { if (h !== 12) h += 12; }
    onChange(`${String(h).padStart(2, '0')}:${newM}`);
  };

  const selSt = {
    background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 5,
    color: v ? 'var(--text-1)' : 'var(--text-3)', fontSize: 13, padding: '7px 2px',
    cursor: 'pointer', outline: 'none', textAlignLast: 'center',
  };

  if (typing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {label && <label style={LABEL_ST}>{label}</label>}
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="time"
            autoFocus
            value={v}
            onChange={e => onChange(e.target.value)}
            onBlur={() => setTyping(false)}
            style={{ ...BASE_INPUT, flex: 1 }}
          />
          <button onClick={() => setTyping(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-2)', borderRadius: 5, padding: '0 10px', cursor: 'pointer' }}>✓</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={LABEL_ST}>{label}</label>}
      <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={h12} onChange={e => commit(e.target.value, mStr || '00', ampm)} style={{ ...selSt, width: 46 }}>
          {!h12 && <option value="">—</option>}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(h => (
            <option key={h} value={String(h)}>{h}</option>
          ))}
        </select>
        <span style={{ color: 'var(--text-3)', fontWeight: 700, fontSize: 14 }}>:</span>
        <select value={mStr} onChange={e => commit(h12 || '12', e.target.value, ampm)} style={{ ...selSt, width: 46 }}>
          {['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={ampm} onChange={e => commit(h12 || '12', mStr || '00', e.target.value)} style={{ ...selSt, width: 48 }}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
        <button
          onClick={() => {
            const n = new Date();
            onChange(`${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`);
          }}
          style={{ fontSize: 11, color: '#4f46e5', background: 'transparent', border: '1px solid rgba(79,70,229,0.3)', borderRadius: 4, padding: '5px 8px', cursor: 'pointer', flexShrink: 0 }}
        >
          Now
        </button>
        <button
          onClick={() => setTyping(true)}
          title="Type exact time"
          style={{ fontSize: 11, color: 'var(--text-3)', background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, padding: '5px 8px', cursor: 'pointer', flexShrink: 0 }}
        >
          ···
        </button>
        {v && (
          <button onClick={() => onChange('')} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 15, padding: '0 2px', lineHeight: 1 }}>✕</button>
        )}
      </div>
    </div>
  );
}

// ── LocationSelect ────────────────────────────────────────────────────────────
// Searchable grouped dropdown for IV/anatomical locations. Accepts free-text too.

const IV_LOCATIONS = [
  { group: 'Antecubital', sites: ['R antecubital', 'L antecubital'] },
  { group: 'Forearm', sites: ['R forearm (dorsal)', 'L forearm (dorsal)', 'R forearm (volar)', 'L forearm (volar)'] },
  { group: 'Hand', sites: ['R hand (dorsal)', 'L hand (dorsal)'] },
  { group: 'Wrist', sites: ['R wrist', 'L wrist'] },
  { group: 'Foot / Ankle', sites: ['R foot (dorsal)', 'L foot (dorsal)', 'R ankle', 'L ankle'] },
  { group: 'Central Access', sites: ['PICC line', 'Central line (R IJ)', 'Central line (L IJ)', 'Central line (subclavian)', 'Central line (femoral)', 'Port-a-cath', 'Midline catheter'] },
];

export function LocationSelect({ label = 'Location', value, onChange, placeholder = 'Search or type location…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', h, true);
    return () => document.removeEventListener('pointerdown', h, true);
  }, [open]);

  const filtered = IV_LOCATIONS.map(g => ({
    ...g,
    sites: g.sites.filter(s => s.toLowerCase().includes(query.toLowerCase())),
  })).filter(g => g.sites.length > 0);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {label && <label style={LABEL_ST}>{label}</label>}
      <input
        style={{ ...BASE_INPUT, width: '100%', boxSizing: 'border-box' }}
        value={open ? query : (value || '')}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(value || ''); }}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); }}
        onBlur={() => setTimeout(() => { setOpen(false); setQuery(''); }, 160)}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: 'var(--navy-2)', border: '1px solid #4f46e5', borderRadius: 6,
          maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 2,
        }}>
          {filtered.map(g => (
            <div key={g.group}>
              <div style={{ padding: '3px 10px', fontSize: 9, color: 'var(--text-3)', background: 'var(--navy-4)', textTransform: 'uppercase', position: 'sticky', top: 0, letterSpacing: '0.07em' }}>
                {g.group}
              </div>
              {g.sites.map(site => (
                <div
                  key={site}
                  onMouseDown={e => { e.preventDefault(); onChange(site); setOpen(false); setQuery(''); }}
                  style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: site === value ? '#c4b5fd' : '#aaa', background: site === value ? 'rgba(79,70,229,0.2)' : 'transparent' }}
                >
                  {site}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MedSelect ─────────────────────────────────────────────────────────────────
// Searchable medication dropdown with dose hints. Accepts free-text too.

const ASC_MEDS = [
  { group: 'Opioids / Analgesics', drugs: [
    { name: 'Fentanyl', hint: '25–100 mcg IV' },
    { name: 'Morphine', hint: '2–4 mg IV' },
    { name: 'Hydromorphone', hint: '0.2–0.4 mg IV' },
    { name: 'Oxycodone', hint: '5–10 mg PO' },
    { name: 'Tramadol', hint: '50–100 mg PO' },
    { name: 'Ketorolac', hint: '15–30 mg IV/IM' },
    { name: 'Acetaminophen', hint: '1000 mg IV/PO' },
    { name: 'Celecoxib', hint: '200–400 mg PO' },
    { name: 'Gabapentin', hint: '300–600 mg PO' },
  ]},
  { group: 'Induction / Sedation', drugs: [
    { name: 'Propofol', hint: '1–2.5 mg/kg IV' },
    { name: 'Ketamine', hint: '0.5–2 mg/kg IV' },
    { name: 'Etomidate', hint: '0.3 mg/kg IV' },
    { name: 'Midazolam', hint: '1–5 mg IV' },
    { name: 'Dexmedetomidine', hint: '0.5–1 mcg/kg IV' },
    { name: 'Lorazepam', hint: '1–4 mg IV' },
    { name: 'Diazepam', hint: '5–10 mg PO' },
  ]},
  { group: 'Neuromuscular Blockers', drugs: [
    { name: 'Succinylcholine', hint: '1–1.5 mg/kg IV' },
    { name: 'Rocuronium', hint: '0.6–1.2 mg/kg IV' },
    { name: 'Vecuronium', hint: '0.1 mg/kg IV' },
    { name: 'Cisatracurium', hint: '0.1–0.2 mg/kg IV' },
  ]},
  { group: 'Reversal Agents', drugs: [
    { name: 'Neostigmine', hint: '0.04–0.07 mg/kg IV' },
    { name: 'Sugammadex', hint: '2–4 mg/kg IV' },
    { name: 'Naloxone', hint: '0.04–0.4 mg IV' },
    { name: 'Flumazenil', hint: '0.1–0.2 mg IV' },
  ]},
  { group: 'Antiemetics', drugs: [
    { name: 'Ondansetron', hint: '4 mg IV' },
    { name: 'Dexamethasone', hint: '4–8 mg IV' },
    { name: 'Metoclopramide', hint: '10 mg IV' },
    { name: 'Promethazine', hint: '12.5–25 mg IV' },
    { name: 'Scopolamine patch', hint: '1.5 mg transdermal' },
    { name: 'Droperidol', hint: '0.625–1.25 mg IV' },
  ]},
  { group: 'Antibiotics', drugs: [
    { name: 'Cefazolin', hint: '1–2 g IV' },
    { name: 'Clindamycin', hint: '600–900 mg IV' },
    { name: 'Vancomycin', hint: '15–20 mg/kg IV' },
    { name: 'Gentamicin', hint: '5 mg/kg IV' },
  ]},
  { group: 'Cardiovascular', drugs: [
    { name: 'Ephedrine', hint: '5–25 mg IV' },
    { name: 'Phenylephrine', hint: '50–200 mcg IV' },
    { name: 'Atropine', hint: '0.4–1 mg IV' },
    { name: 'Epinephrine', hint: '10–100 mcg IV' },
    { name: 'Glycopyrrolate', hint: '0.1–0.2 mg IV' },
    { name: 'Labetalol', hint: '5–20 mg IV' },
    { name: 'Metoprolol', hint: '2.5–5 mg IV' },
  ]},
  { group: 'Local Anesthetics', drugs: [
    { name: 'Lidocaine 1%', hint: '≤3–5 mg/kg' },
    { name: 'Bupivacaine 0.25%', hint: '≤2 mg/kg' },
    { name: 'Bupivacaine 0.5%', hint: '≤2 mg/kg' },
    { name: 'Ropivacaine 0.5%', hint: '≤3 mg/kg' },
  ]},
];

export function MedSelect({ label = 'Drug', value, onChange, placeholder = 'Search medication…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', h, true);
    return () => document.removeEventListener('pointerdown', h, true);
  }, [open]);

  // When no query, show ASC-specific groups; when query typed, search full DB
  const ascFiltered = !query
    ? ASC_MEDS.map(g => ({ category: g.group, items: g.drugs.map(d => ({ name: d.name, hint: d.hint, isControlled: false })) }))
    : [];
  const dbGroups = query ? groupByCategory(searchMedications(query, 20)) : [];
  const groups = query ? dbGroups : ascFiltered;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {label && <label style={LABEL_ST}>{label}</label>}
      <input
        style={{ ...BASE_INPUT, width: '100%', boxSizing: 'border-box' }}
        value={value || ''}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(value || ''); setOpen(true); }}
        onBlur={() => setTimeout(() => { setOpen(false); setQuery(''); }, 160)}
      />
      {open && groups.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: 'var(--navy-2)', border: '1px solid #4f46e5', borderRadius: 6,
          maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 2,
        }}>
          {groups.map(g => (
            <div key={g.category}>
              <div style={{ padding: '3px 10px', fontSize: 9, color: 'var(--text-3)', background: 'var(--navy-4)', textTransform: 'uppercase', position: 'sticky', top: 0, letterSpacing: '0.07em' }}>
                {g.category}
              </div>
              {g.items.map(item => (
                <div
                  key={item.name}
                  onMouseDown={e => { e.preventDefault(); onChange(item.name); setOpen(false); }}
                  style={{
                    padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                    color: item.name === value ? '#c4b5fd' : '#aaa',
                    background: item.name === value ? 'rgba(79,70,229,0.2)' : 'transparent',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span>{item.name}</span>
                    {item.brandNames?.length > 0 && (
                      <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 6 }}>({item.brandNames[0]})</span>
                    )}
                  </div>
                  {item.hint && <span style={{ fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>{item.hint}</span>}
                  {item.isControlled && <span style={{ fontSize: 9, color: '#fca5a5', fontWeight: 700, flexShrink: 0 }}>⚠ {item.schedule}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
